import CloseButton from '@app/components/CloseButton';
import DIMENSIONS from '@app/config/dimensions';
import { useClientArea } from '@app/contexts/ClientArea';
import { useClock } from '@app/contexts/Clock';
import { useRestaurantContext } from '@app/pages/Restaurant/context';
import { APIAddressesShared } from '@app/services/addresses-shared-api';
import { createSharedAddressFromAddress } from '@app/services/api-forms';
import { APICliqueiAchei } from '@app/services/cliqueiachei-api';
import { AddressStorage, UserStorage } from '@app/services/storage';
import componentsClasses from '@app/styles/components.module.css';
import formsClasses from '@app/styles/forms.module.css';
import { Address, User } from '@app/types';
import { getCityByUrl } from '@app/utils/city';
import { useIsTabletOrMobileScreenSize } from '@app/utils/media-query';
import { yupResolver } from '@hookform/resolvers/yup';
import { Checkbox, CircularProgress } from '@material-ui/core';
import cls from 'classnames';
import { format, isValid } from 'date-fns';
import React, { FocusEvent, useEffect, useMemo, useRef, useState } from 'react';
import { DeepRequired, FieldErrorsImpl, useForm } from 'react-hook-form';
import { useDialogAlert } from '../DialogAlert';
import MaskedInput from '../MaskedInput';
import ModalResponsive from '../ModalResponsive';
import CellphoneInput from './CellphoneInput';
import CEPHelperText from './CEPHelperText';
import CitySelector from './CitySelector';
import { searchCityByName } from './CitySelector/helpers';
import {
  createAddressFromFormData,
  createUserFromFromData,
  FormDataSchema,
  getYupSchemaResolver,
  useCellphoneSearchByClient,
  useCEPSearch,
} from './helpers';
import {
  BirthdayLabel,
  CheckboxWrapper,
  Content,
  Form,
  FormGroup,
  FormRow,
  LastUsageLabel,
  Title,
  TitleBox,
} from './styles';
import classes from './styles.module.css';

type Props = {
  isVisible: boolean;
  onClose: () => void;
  user?: User | null;
  address?: Address | null;
  onSave: (newAddress: Address | null, user: User | null) => void;
  hiddenAddressInputs?: boolean;
  hiddenUserInputs?: boolean;
  buttonLabel?: string;
};

export default function UserInformationsManagerModal({
  isVisible,
  onClose,
  user,
  address,
  onSave,
  hiddenAddressInputs = false,
  hiddenUserInputs = false,
  buttonLabel = 'Salvar',
}: Props) {
  const clock = useClock();

  const [dontHaveNumber, setDontHaveNumber] = useState(false);

  const { dialogRef, DialogAlert } = useDialogAlert();

  const {
    handleSubmit,
    formState: { errors },
    register,
    watch,
    setValue,
    reset,
  } = useForm<FormDataSchema>({
    resolver: yupResolver(getYupSchemaResolver(hiddenAddressInputs, hiddenUserInputs, dontHaveNumber)),
    shouldFocusError: true,
  });

  const isTabletOrMobile = useIsTabletOrMobileScreenSize();

  const contentRef = useRef<HTMLDivElement>(null);

  const { isLoggedIn: isLoggedInClientArea, client } = useClientArea();
  const { primaryColor, secondaryColor, restaurant, cliqueiAcheiData } = useRestaurantContext();
  const { cepInformation, isFetchingCepInformation, hasErrorOnSearch: hasErrorOnCEPSearch } = useCEPSearch(
    watch('cep') ?? ''
  );
  const [shouldBlockCitySelector, setShouldBlockCitySelector] = useState(false);

  const [savingOnServer, setSavingOnServer] = useState(false);

  const cellphone = watch('cellphone');

  const {
    cliqueiAcheiUserFound,
    isSearchingByUser,
    sharedAddressFound,
    userNotFound,
    resetSearchForCellphoneStates,
  } = useCellphoneSearchByClient(cellphone, isLoggedInClientArea, client, setValue, watch, reset, dialogRef);

  const inputsDisabledBeforeSearch = Boolean(
    isLoggedInClientArea && (isSearchingByUser || (cellphone ?? '').length !== 11)
  );

  useEffect(() => {
    if (isVisible) {
      // Force open with data updated
      reset({ ...address, ...user });

      resetSearchForCellphoneStates();
      if (restaurant.Configuracoes?.entregaEmOutrasCidades) {
        setShouldBlockCitySelector(false);
      } else {
        const city = getCityByUrl(cliqueiAcheiData.Url_Cidade);
        if (!city) return;
        setValue('city', city);
        setShouldBlockCitySelector(true);
      }
    }
  }, [isVisible]);

  useEffect(() => {
    if (cepInformation && !hasErrorOnCEPSearch) {
      const cities = searchCityByName(cepInformation.city, cepInformation.state);
      if (cities.length > 0) setValue('city', cities[0]);
      if (Boolean(cepInformation.neighborhood)) setValue('neighborhood', cepInformation.neighborhood);
      if (Boolean(cepInformation.street)) setValue('street', cepInformation.street);
    }
  }, [cepInformation, hasErrorOnCEPSearch]);

  useEffect(() => {
    if (dontHaveNumber) setValue('number', '');
  }, [dontHaveNumber]);

  async function handleSubmitForm(data: FormDataSchema) {
    if (savingOnServer) return;

    const newAddress = getNewAddressData();
    const newUser = getNewUserData();

    saveAddressInStorageIfNecessary();
    saveUserInStorageIfNecessary();

    if (isLoggedInClientArea) {
      console.log('Saving in database...');
      if (!(await saveUserDataOnServer())) return;
    }

    onSave(newAddress, newUser);

    function getNewAddressData() {
      if (hiddenAddressInputs) return null;
      return createAddressFromFormData(data, hasErrorOnCEPSearch, address);
    }

    function getNewUserData() {
      if (hiddenUserInputs) return null;
      return createUserFromFromData(data);
    }

    function saveAddressInStorageIfNecessary() {
      if (newAddress && !isLoggedInClientArea) AddressStorage.save(newAddress);
    }

    function saveUserInStorageIfNecessary() {
      if (newUser && !isLoggedInClientArea) UserStorage.save(newUser);
    }

    async function saveUserDataOnServer() {
      setSavingOnServer(true);
      let savedWithSuccess = true;
      try {
        let deviceId;
        if (userNotFound) {
          deviceId = await APICliqueiAchei.createNewUser({ ...newUser });
        } else {
          deviceId = cliqueiAcheiUserFound?.DeviceId ?? '';
        }

        if (isLoggedInClientArea) await saveAddressOnServer(deviceId);
      } catch (error) {
        console.warn(error);
        dialogRef.current?.alert({
          title: 'Ocorreu um erro',
          message: 'Não conseguimos salvar os dados do pedido',
        });
        savedWithSuccess = false;
      } finally {
        setSavingOnServer(false);
      }
      return savedWithSuccess;
    }

    async function saveAddressOnServer(deviceId: string) {
      if (!newAddress || !client) return;
      const sharedAddress = createSharedAddressFromAddress(newAddress, {
        savedByRestaurantId: sharedAddressFound?.savedByRestaurantId ?? restaurant.id,
        savedByRestaurantName: sharedAddressFound?.savedByRestaurantName ?? cliqueiAcheiData.NomePuro,
        lastUsageAt: clock.now()?.getTime() ?? Date.now(),
        lastUsageByRestaurantId: restaurant.id,
        lastUsageByRestaurantName: cliqueiAcheiData.NomePuro,
        userDeviceId: deviceId,
      });
      await APIAddressesShared.save(sharedAddress);
    }
  }

  function handleOnClose() {
    reset();
    onClose();
  }

  function handleCitySelectorClickWhenDisabled() {
    if (!restaurant.Configuracoes?.entregaEmOutrasCidades) {
      dialogRef.current?.alert({
        title: 'Você não pode alterar a cidade',
        message: `Este estabelecimento faz entregas somente em ${watch('city.Nom_Cidade')}`,
      });
    }
  }

  function handleSubmitError(errors: FieldErrorsImpl<DeepRequired<FormDataSchema>>) {
    if (errors.cellphone) errors.cellphone.ref?.focus?.();
  }

  const inputErrorClassName = cls(formsClasses.errorMessage, classes.errorMessage);

  function handleScrollOnFocus(event: FocusEvent<HTMLInputElement>) {
    event.preventDefault();
    if (contentRef.current) contentRef.current.scrollTop = event.target.offsetTop - DIMENSIONS.APP_BAR.HEIGHT - 10;
  }

  const lastUsageDateLabel = useMemo(() => {
    if (!sharedAddressFound) return null;
    if (!sharedAddressFound.lastUsageAt) return null;
    if (!isValid(sharedAddressFound.lastUsageAt)) return null;

    return format(sharedAddressFound.lastUsageAt, " 'dia' dd/MM/yyyy 'às' HH:mm");
  }, [sharedAddressFound]);

  const hasNameError = Boolean(errors.name && !inputsDisabledBeforeSearch);
  const hasCepError = Boolean(errors.cep && !inputsDisabledBeforeSearch);
  const hasStreetError = Boolean(errors.street && !inputsDisabledBeforeSearch);
  const hasNumberError = Boolean(errors.number && !inputsDisabledBeforeSearch);
  const hasComplementError = Boolean(errors.complement && !inputsDisabledBeforeSearch);
  const hasLandmarkError = Boolean(errors.landmark && !inputsDisabledBeforeSearch);
  const hasNeighborhoodError = Boolean(errors.neighborhood && !inputsDisabledBeforeSearch);
  const hasCityError = Boolean(errors.city && !inputsDisabledBeforeSearch);

  const cellphoneErrorMessage = isLoggedInClientArea ? 'Informe o WhatsApp do cliente' : errors.cellphone?.message;
  const nameErrorMessage = isLoggedInClientArea ? 'Informe o nome do cliente' : errors.name?.message;

  const isBirthdayOfUserFound = Boolean(
    cellphone?.length === 11 &&
    cliqueiAcheiUserFound &&
    cliqueiAcheiUserFound.MesNascimento === clock.now()?.getMonth() &&
    cliqueiAcheiUserFound.DiaNascimento === clock.now()?.getDate()
  );

  return (
    <ModalResponsive
      open={isVisible}
      onClose={handleOnClose}
      appBarConfig={{ title: '', color: primaryColor, tintColor: secondaryColor }}
      desktopWidthContent={500}
      desktopMaxHeightContent="90%"
      desktopHeightContent="fit-content"
      hiddenDesktopBar={true}
    >
      <Content ref={contentRef}>
        <Form onSubmit={handleSubmit(handleSubmitForm, handleSubmitError)} autoComplete="no">
          {!hiddenUserInputs && (
            <>
              <TitleBox>
                <Title>{isLoggedInClientArea ? 'Informações do Cliente' : 'Suas informações'}</Title>
                {!isTabletOrMobile && <CloseButton onClick={handleOnClose} />}
              </TitleBox>

              <CellphoneInput
                placeholder={isLoggedInClientArea ? 'Número do WhatsApp do Cliente' : 'Seu número de WhatsApp'}
                value={watch('cellphone')}
                onValueChange={(value) => setValue('cellphone', value, { shouldValidate: Boolean(errors.cellphone) })}
                className={cls(errors.cellphone && formsClasses.inputError)}
                onFocus={handleScrollOnFocus}
                isSearchingByUser={isSearchingByUser}
                ErrorMessage={
                  errors.cellphone && <span className={inputErrorClassName}>{cellphoneErrorMessage ?? ''}</span>
                }
                hiddenClearButton={Boolean(
                  !isLoggedInClientArea || (isLoggedInClientArea && (watch('cellphone') ?? '').length === 0)
                )}
                onClear={() => reset({ cellphone: '', city: shouldBlockCitySelector ? watch('city') : null })}
                registerInput={() => register('cellphone')}
              />

              {isBirthdayOfUserFound && (
                <BirthdayLabel>
                  🎉🎂🥳🎁 Hoje é aniversário deste Cliente! E uma grande oportunidade para você fidelizá-lo tornando
                  seu dia mais especial. Porque não envia um brinde?
                </BirthdayLabel>
              )}

              <input
                placeholder={isLoggedInClientArea ? 'Nome do Cliente' : 'Seu nome'}
                className={cls(formsClasses.textField, hasNameError && formsClasses.inputError)}
                {...register('name')}
                onFocus={handleScrollOnFocus}
                disabled={inputsDisabledBeforeSearch}
                data-cy="user-informations-name-input"
                autoComplete="no"
              />
              {hasNameError && <span className={inputErrorClassName}>{nameErrorMessage ?? ''}</span>}
            </>
          )}

          {!hiddenAddressInputs && (
            <>
              <TitleBox>
                <Title>Endereço de entrega</Title>
                {hiddenUserInputs && !isTabletOrMobile && <CloseButton onClick={handleOnClose} />}
              </TitleBox>
              {sharedAddressFound?.lastUsageByRestaurantName && (
                <LastUsageLabel>
                  Endereço usado recentemente{' '}
                  {sharedAddressFound.lastUsageByRestaurantId === restaurant.id
                    ? 'neste estabelecimento'
                    : `na ${sharedAddressFound.lastUsageByRestaurantName}`}
                  {lastUsageDateLabel && lastUsageDateLabel}.
                </LastUsageLabel>
              )}
              {!shouldBlockCitySelector && (
                <MaskedInput
                  type="text"
                  inputMode="numeric"
                  value={watch('cep')}
                  onAccept={(value) => setValue('cep', value, { shouldValidate: Boolean(errors.cep?.message) })}
                  className={cls(
                    formsClasses.textField,
                    Boolean(hasCepError || hasErrorOnCEPSearch) && formsClasses.inputError
                  )}
                  placeholder="CEP"
                  unmask
                  mask={'00.000-000'}
                  onFocus={handleScrollOnFocus}
                  autoComplete="no"
                  disabled={inputsDisabledBeforeSearch}
                  data-cy="user-informations-cep-input"
                />
              )}
              <CEPHelperText
                isFetching={isFetchingCepInformation}
                hasErrorOnSearch={hasErrorOnCEPSearch}
                invalidCEPMessage={errors.cep?.message ?? ''}
              />

              <input
                className={cls(formsClasses.textField, hasStreetError && formsClasses.inputError)}
                placeholder="Rua / Avenida"
                {...register('street')}
                autoComplete="no"
                disabled={isFetchingCepInformation || inputsDisabledBeforeSearch}
                onFocus={handleScrollOnFocus}
                data-cy="user-informations-street-input"
              />
              {hasStreetError && <span className={inputErrorClassName}>{errors.street?.message}</span>}

              <FormRow>
                <FormGroup>
                  <input
                    type="number"
                    className={cls(formsClasses.textField, hasNumberError && formsClasses.inputError)}
                    placeholder="Número"
                    {...register('number')}
                    disabled={isFetchingCepInformation || dontHaveNumber || inputsDisabledBeforeSearch}
                    onFocus={handleScrollOnFocus}
                    data-cy="user-informations-number-input"
                    autoComplete="no"
                  />
                  {hasNumberError && <span className={formsClasses.errorMessage}>{errors.number?.message}</span>}
                </FormGroup>
                <CheckboxWrapper>
                  <Checkbox
                    id="dont-need-number"
                    style={{ color: primaryColor }}
                    checked={dontHaveNumber}
                    onChange={(e) => setDontHaveNumber(e.target.checked)}
                  />
                  <label htmlFor="dont-need-number">S/N</label>
                </CheckboxWrapper>
              </FormRow>
              <input
                className={cls(formsClasses.textField, hasComplementError && formsClasses.inputError)}
                placeholder="Complemento (opcional)"
                {...register('complement')}
                disabled={isFetchingCepInformation || inputsDisabledBeforeSearch}
                onFocus={handleScrollOnFocus}
                data-cy="user-informations-complement-input"
              />
              {hasComplementError && <span className={inputErrorClassName}>{errors.complement?.message}</span>}

              <input
                className={cls(formsClasses.textField, hasLandmarkError && formsClasses.inputError)}
                placeholder="Ponto de referência (opcional)"
                {...register('landmark')}
                disabled={isFetchingCepInformation || inputsDisabledBeforeSearch}
                onFocus={handleScrollOnFocus}
                data-cy="user-informations-landmark-input"
              />
              {hasLandmarkError && <span className={inputErrorClassName}>{errors.landmark?.message}</span>}

              <input
                className={cls(formsClasses.textField, hasNeighborhoodError && formsClasses.inputError)}
                placeholder="Bairro"
                {...register('neighborhood')}
                disabled={isFetchingCepInformation || inputsDisabledBeforeSearch}
                onFocus={handleScrollOnFocus}
                data-cy="user-informations-neighborhood-input"
              />
              {hasNeighborhoodError && <span className={inputErrorClassName}>{errors.neighborhood?.message}</span>}

              <CitySelector
                defaultValue={address?.city ?? null}
                city={watch('city') ?? null}
                onSelectCity={(city) => setValue('city', city, { shouldValidate: true })}
                inputClassName={cls(formsClasses.textField, hasCityError && formsClasses.inputError)}
                ErrorMessageComponent={
                  hasCityError && <span className={inputErrorClassName}>{errors.city?.message}</span>
                }
                disabled={shouldBlockCitySelector || inputsDisabledBeforeSearch}
                onClickWhenDisabled={handleCitySelectorClickWhenDisabled}
              />
            </>
          )}

          <button className={componentsClasses.successButton} type="submit" data-cy="user-informations-save-button">
            {savingOnServer ? <CircularProgress size={20} style={{ color: '#fff' }} /> : <span>{buttonLabel}</span>}
          </button>
        </Form>
        {DialogAlert}
      </Content>
    </ModalResponsive>
  );
}
